import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fetchAdminInvoiceAnalytics } from '../../services/admin/adminInvoiceApi';
import { fetchConferenceSubscriptionTracker } from '../../services/admin/subscriptionTrackerApi';
import { CONFERENCE_TRACKER_SPORTS } from '../../types/admin/subscriptionTracker';
import {
  AdminInvoiceAnalyticsChaseRow,
  AdminInvoiceAnalyticsResponse,
  InvoiceAgingBucket,
  InvoiceAnalyticsGranularity,
  InvoiceAnalyticsSource,
} from '../../types/adminInvoice';
import {
  formatAnalyticsCountLine,
  formatCollectionRate,
  formatInvoiceMoney,
  isAnalyticsPeriodEmpty,
} from '../../utils/admin/formatInvoiceMoney';
import { formatSubscriptionDate, getSportLabel } from '../../utils/admin/formatSubscriptionDate';
import Spinner from '../Spinner';
import {
  INVOICE_ANALYTICS_PRESETS,
  InvoiceAnalyticsPreset,
  rangeForPreset,
  last6MonthsRange,
} from './invoiceAnalyticsDates';

const CONFERENCE_SPORT_LABELS: Record<string, string> = {
  MFB: "Men's Football",
  MBB: "Men's Basketball",
  WBB: "Women's Basketball",
  MIH: "Men's Ice Hockey",
  WIH: "Women's Ice Hockey",
  MSO: "Men's Soccer",
  WSO: "Women's Soccer",
  MVB: "Men's Volleyball",
  WVB: "Women's Volleyball",
};

function conferenceSportLabel(code: string): string {
  return CONFERENCE_SPORT_LABELS[code] ?? getSportLabel(code);
}

const SPORT_OPTIONS = CONFERENCE_TRACKER_SPORTS.map((code) => ({
  value: code,
  label: `${conferenceSportLabel(code)} (${code})`,
}));

const AGING_LABELS: Record<string, string> = {
  not_due: 'Not due',
  due_soon: 'Due soon',
  due_today: 'Due today',
  overdue: 'Overdue',
};

const SOURCE_SCOPES: { value: InvoiceAnalyticsSource; label: string }[] = [
  { value: 'all', label: 'Overall' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'checkout', label: 'Checkout' },
];

function emptyRangeCopy(source: InvoiceAnalyticsSource): string {
  if (source === 'checkout') return 'No checkout payments in this range';
  if (source === 'invoice') return 'No invoices generated in this range';
  return 'No invoice or checkout payments in this range';
}

function formatYmdLabel(ymd: string): string {
  if (!ymd) return '—';
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const FILTER_CONTROL =
  'w-full min-w-0 p-2 border border-gray-200 rounded-md text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50';

function formatBucketLabel(bucket: string, granularity?: string | null): string {
  if (!bucket) return '—';
  // Month buckets often look like 2026-09
  if (/^\d{4}-\d{2}$/.test(bucket) || granularity === 'month') {
    const [y, m] = bucket.split('-').map(Number);
    if (y && m) {
      const d = new Date(Date.UTC(y, m - 1, 1));
      return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
    }
  }
  // Day buckets YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(bucket)) {
    const d = new Date(`${bucket.slice(0, 10)}T00:00:00Z`);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
    }
  }
  return bucket;
}

function chaseMatchesAging(
  row: AdminInvoiceAnalyticsChaseRow,
  bucket: InvoiceAgingBucket
): boolean {
  const overdue = (row.days_overdue ?? 0) > 0;
  const until = row.days_until_due;
  switch (bucket) {
    case 'overdue':
      return overdue;
    case 'due_today':
      return !overdue && until === 0;
    case 'not_due':
      return !overdue && until != null && until > 0;
    case 'due_soon':
      // Safe heuristic: positive days_until_due in a near window; prefer API aging totals for display
      return !overdue && until != null && until > 0 && until <= 7;
    default:
      return true;
  }
}

interface AdminInvoiceAnalyticsTabProps {
  onFocusInvoiceUser: (search: string) => void;
}

const AdminInvoiceAnalyticsTab: React.FC<AdminInvoiceAnalyticsTabProps> = ({
  onFocusInvoiceUser,
}) => {
  const initialRange = last6MonthsRange();
  const [preset, setPreset] = useState<InvoiceAnalyticsPreset>('last_6_months');
  const [dateFrom, setDateFrom] = useState(initialRange.date_from);
  const [dateTo, setDateTo] = useState(initialRange.date_to);
  const [sportCode, setSportCode] = useState('');
  const [conferenceName, setConferenceName] = useState('');
  const [conferenceOptions, setConferenceOptions] = useState<string[]>([]);
  const [conferencesLoading, setConferencesLoading] = useState(false);
  const [granularityOverride, setGranularityOverride] =
    useState<InvoiceAnalyticsGranularity | ''>('');
  const [source, setSource] = useState<InvoiceAnalyticsSource>('all');

  const [data, setData] = useState<AdminInvoiceAnalyticsResponse | null>(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [appliedKey, setAppliedKey] = useState<string | null>(null);

  const [agingFilter, setAgingFilter] = useState<InvoiceAgingBucket | null>(null);
  const [focusUnpaid, setFocusUnpaid] = useState(false);
  const chaseRef = useRef<HTMLDivElement>(null);

  const viewKey = `${source}|${dateFrom}|${dateTo}|${granularityOverride}|${sportCode}|${conferenceName}`;
  const resultsReady = appliedKey === viewKey && data != null;

  const prevSportRef = useRef(sportCode);

  useEffect(() => {
    const sportChanged = prevSportRef.current !== sportCode;
    prevSportRef.current = sportCode;
    if (sportChanged) {
      setConferenceName('');
    }

    const controller = new AbortController();
    setConferencesLoading(true);
    const sportsToLoad = sportCode ? [sportCode] : [...CONFERENCE_TRACKER_SPORTS];

    (async () => {
      const names = new Set<string>();
      for (const code of sportsToLoad) {
        if (controller.signal.aborted) return;
        const result = await fetchConferenceSubscriptionTracker(
          { sport_code: code, limit: 200, offset: 0 },
          controller.signal
        );
        if (controller.signal.aborted || result.aborted) return;
        for (const c of result.data?.conferences ?? []) {
          if (c.conference_name) names.add(c.conference_name);
        }
      }
      setConferenceOptions([...names].sort((a, b) => a.localeCompare(b)));
      setConferencesLoading(false);
    })();

    return () => controller.abort();
  }, [sportCode]);

  useEffect(() => {
    const controller = new AbortController();
    setFetching(true);
    setError(null);

    (async () => {
      const result = await fetchAdminInvoiceAnalytics(
        {
          date_from: dateFrom,
          date_to: dateTo,
          source,
          ...(granularityOverride ? { granularity: granularityOverride } : {}),
          ...(sportCode.trim() ? { sport_code: sportCode.trim() } : {}),
          ...(conferenceName.trim() ? { conference_name: conferenceName.trim() } : {}),
        },
        controller.signal
      );

      if (controller.signal.aborted || result.aborted) return;

      if (result.data) {
        setData(result.data);
        setAppliedKey(viewKey);
        setError(null);
      } else {
        setData(null);
        setAppliedKey(viewKey);
        setError(result.error || 'Failed to load invoice analytics');
      }
      setFetching(false);
    })();

    return () => controller.abort();
  }, [dateFrom, dateTo, source, granularityOverride, sportCode, conferenceName, refreshTick, viewKey]);

  const applyPreset = (next: InvoiceAnalyticsPreset) => {
    setPreset(next);
    setAgingFilter(null);
    setFocusUnpaid(false);
    if (next === 'custom') return;
    const range = rangeForPreset(next);
    setDateFrom(range.date_from);
    setDateTo(range.date_to);
  };

  const applySource = (next: InvoiceAnalyticsSource) => {
    if (next === source) return;
    setSource(next);
    setAgingFilter(null);
    setFocusUnpaid(false);
  };

  const currency = data?.currency || 'usd';
  const kpis = resultsReady ? data?.kpis : undefined;
  const isCheckout = source === 'checkout';
  const showInvoiceCollections = !isCheckout;
  const openNow = resultsReady && showInvoiceCollections ? data?.open_now : undefined;
  const periodEmpty = !!kpis && isAnalyticsPeriodEmpty(source, kpis);
  const outstandingAmount = isCheckout ? 0 : (kpis?.outstanding_amount ?? 0);
  const outstandingCount = isCheckout ? 0 : (kpis?.outstanding_count ?? 0);
  const emptyCopy = emptyRangeCopy(source);

  const dueSoon = data?.aging?.find((a) => a.bucket === 'due_soon');
  const overdueAging = data?.aging?.find((a) => a.bucket === 'overdue');
  const outstandingTone: 'default' | 'amber' | 'red' = isCheckout
    ? 'default'
    : (overdueAging && (overdueAging.count > 0 || overdueAging.amount > 0)) ||
        (openNow?.overdue_count ?? 0) > 0
      ? 'red'
      : dueSoon && (dueSoon.count > 0 || dueSoon.amount > 0)
        ? 'amber'
        : 'default';

  const serverGranularity =
    resultsReady && typeof data?.filters?.granularity === 'string' ? data.filters.granularity : null;

  const chartData = useMemo(
    () =>
      (resultsReady ? data?.series ?? [] : []).map((p) => ({
        ...p,
        label: formatBucketLabel(p.bucket, serverGranularity),
      })),
    [data?.series, serverGranularity, resultsReady]
  );

  const filteredChase = useMemo(() => {
    const rows = resultsReady ? data?.chase ?? [] : [];
    let next = rows;
    if (agingFilter && agingFilter !== 'due_soon') {
      next = next.filter((r) => chaseMatchesAging(r, agingFilter));
    } else if (agingFilter === 'due_soon') {
      // Keep display-only if heuristic is weak: still filter near-due unpaid
      const filtered = next.filter((r) => chaseMatchesAging(r, 'due_soon'));
      next = filtered.length > 0 ? filtered : next;
    }
    if (focusUnpaid) {
      next = next.filter((r) => (r.days_overdue ?? 0) > 0 || (r.status || '').toLowerCase() !== 'paid');
      // Prefer overdue highlight order already from API; just mark focus
    }
    return next;
  }, [data?.chase, agingFilter, focusUnpaid, resultsReady]);

  const handleOpenNowClick = () => {
    setFocusUnpaid(true);
    setAgingFilter('overdue');
    chaseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSportBarClick = (code: string) => {
    setSportCode((prev) => (prev === code ? '' : code));
  };

  const openNowTone =
    (openNow?.overdue_count ?? 0) > 0
      ? 'red'
      : (openNow?.outstanding_count ?? 0) > 0 || (openNow?.outstanding_amount ?? 0) > 0
        ? 'amber'
        : 'neutral';

  return (
    <div className="p-4 bg-white shadow-md rounded-md mb-6 space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold text-gray-800">Payment Analytics</h2>
          <p className="text-gray-500 text-sm mt-0.5">
            Payments and unpaid invoices. Open now ignores the date range.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="inline-flex rounded-lg bg-gray-100 p-0.5"
            role="group"
            aria-label="Payment source"
          >
            {SOURCE_SCOPES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => applySource(s.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                  source === s.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setRefreshTick((n) => n + 1)}
            disabled={fetching}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
            Period
            <select
              className={FILTER_CONTROL}
              value={preset}
              onChange={(e) => applyPreset(e.target.value as InvoiceAnalyticsPreset)}
            >
              {INVOICE_ANALYTICS_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          <div className="flex flex-col gap-1 sm:col-span-2">
            <span className="text-xs font-medium text-gray-500">Invoiced between</span>
            {preset === 'custom' ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="date"
                  className={FILTER_CONTROL}
                  value={dateFrom}
                  onChange={(e) => {
                    setPreset('custom');
                    setDateFrom(e.target.value);
                  }}
                />
                <span className="text-gray-400 shrink-0">–</span>
                <input
                  type="date"
                  className={FILTER_CONTROL}
                  value={dateTo}
                  onChange={(e) => {
                    setPreset('custom');
                    setDateTo(e.target.value);
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center min-h-[38px] px-2.5 rounded-md border border-gray-200 bg-white text-sm text-gray-800 tabular-nums">
                {formatYmdLabel(dateFrom)}
                <span className="mx-1.5 text-gray-400">–</span>
                {formatYmdLabel(dateTo)}
              </div>
            )}
          </div>

          <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
            Sport
            <select
              className={FILTER_CONTROL}
              value={sportCode}
              onChange={(e) => setSportCode(e.target.value)}
            >
              <option value="">All sports</option>
              {SPORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
            Conference
            <select
              className={FILTER_CONTROL}
              value={conferenceName}
              onChange={(e) => setConferenceName(e.target.value)}
            >
              <option value="">
                {conferencesLoading ? 'All conferences (loading…)' : 'All conferences'}
              </option>
              {conferenceOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
            View by
            <select
              className={FILTER_CONTROL}
              value={granularityOverride || serverGranularity || ''}
              onChange={(e) => {
                const next = e.target.value;
                setGranularityOverride(
                  next === 'day' || next === 'month' ? next : ''
                );
              }}
            >
              <option value="">Auto</option>
              <option value="day">Day</option>
              <option value="month">Month</option>
            </select>
          </label>
        </div>
      </div>

      {error && !fetching && (
        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
      )}

      {fetching && !resultsReady ? (
        <div className="flex justify-center items-center h-48 bg-gray-50 rounded-lg">
          <Spinner size="md" />
          <span className="ml-2 text-gray-600">Loading invoice analytics…</span>
        </div>
      ) : (
        <>
      {/* KPIs */}
      {periodEmpty ? (
        <div className="p-6 bg-gray-50 rounded-lg text-center text-gray-600">
          {emptyCopy}
        </div>
      ) : kpis && isCheckout ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
          <div className="p-4 border border-gray-200 rounded-lg bg-white">
            <div className="text-xs font-semibold uppercase text-gray-500">Paid</div>
            <div className="mt-1 text-2xl font-bold tabular-nums text-green-700">
              {formatInvoiceMoney(kpis.collected_amount, currency)}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {formatAnalyticsCountLine(source, kpis)}
            </p>
          </div>
        </div>
      ) : kpis ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="p-4 border border-gray-200 rounded-lg bg-white">
            <div className="text-xs font-semibold uppercase text-gray-500">Invoiced</div>
            <div className="mt-1 text-2xl font-bold tabular-nums text-blue-700">
              {formatInvoiceMoney(kpis.invoiced_amount, currency)}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {formatAnalyticsCountLine(source, kpis)}
            </p>
          </div>

          <div className="p-4 border border-gray-200 rounded-lg bg-white">
            <div className="text-xs font-semibold uppercase text-gray-500">Collected</div>
            <div className="mt-1 text-2xl font-bold tabular-nums text-green-700">
              {formatInvoiceMoney(kpis.collected_amount, currency)}
              <span className="text-gray-400 font-semibold text-lg"> / </span>
              <span className="text-lg text-gray-700">
                {formatInvoiceMoney(kpis.invoiced_amount, currency)}
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-600">
              Collected {formatInvoiceMoney(kpis.collected_amount, currency)} of{' '}
              {formatInvoiceMoney(kpis.invoiced_amount, currency)}
            </p>
            <p className="mt-0.5 text-xs text-gray-500">{kpis.collected_count} paid</p>
          </div>

          <div
            className={`p-4 border rounded-lg ${
              outstandingTone === 'red'
                ? 'border-red-300 bg-red-50'
                : outstandingTone === 'amber'
                  ? 'border-amber-300 bg-amber-50'
                  : 'border-gray-200 bg-white'
            }`}
          >
            <div className="text-xs font-semibold uppercase text-gray-500">Outstanding</div>
            <div
              className={`mt-1 text-2xl font-bold tabular-nums ${
                outstandingTone === 'red'
                  ? 'text-red-700'
                  : outstandingTone === 'amber'
                    ? 'text-amber-800'
                    : 'text-gray-900'
              }`}
            >
              {formatInvoiceMoney(outstandingAmount, currency)}
            </div>
            <p className="mt-1 text-xs text-gray-500">{outstandingCount} unpaid</p>
          </div>

          <div className="p-4 border border-gray-200 rounded-lg bg-white">
            <div className="text-xs font-semibold uppercase text-gray-500">Collection rate</div>
            <div className="mt-1 text-2xl font-bold tabular-nums text-purple-700">
              {formatCollectionRate(kpis.collection_rate)}
            </div>
            <p className="mt-1 text-xs text-gray-600">
              Collected {formatInvoiceMoney(kpis.collected_amount, currency)} of{' '}
              {formatInvoiceMoney(kpis.invoiced_amount, currency)}
            </p>
          </div>
        </div>
      ) : null}

      {/* Open now — Overall / Invoice only */}
      {showInvoiceCollections && openNow && (
        <button
          type="button"
          onClick={handleOpenNowClick}
          className={`w-full text-left px-4 py-3 rounded-lg border text-sm ${
            openNowTone === 'red'
              ? 'bg-red-50 border-red-300 text-red-900'
              : openNowTone === 'amber'
                ? 'bg-amber-50 border-amber-300 text-amber-900'
                : 'bg-gray-50 border-gray-200 text-gray-700'
          } hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-blue-500`}
        >
          <span className="font-semibold">Open now (any date):</span>{' '}
          {formatInvoiceMoney(openNow.outstanding_amount, currency)} outstanding
          {openNow.outstanding_count != null ? ` (${openNow.outstanding_count})` : ''}
          {' · '}
          {openNow.overdue_count} overdue
          {' · '}
          {openNow.grace_ending_7d_count} grace ending in 7 days
        </button>
      )}

      {/* Trend */}
      <section>
        <h3 className="text-lg font-medium text-gray-900 mb-3">Trend</h3>
        {periodEmpty || chartData.length === 0 ? (
          <div className="h-48 flex items-center justify-center bg-gray-50 rounded-lg text-gray-500">
            {emptyCopy}
          </div>
        ) : (
          <div className="w-full h-72 border border-gray-200 rounded-lg p-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    formatInvoiceMoney(Number(value), currency),
                    name,
                  ]}
                />
                <Legend />
                {isCheckout ? (
                  <Bar dataKey="collected_amount" name="Paid" fill="#16a34a" />
                ) : (
                  <Bar dataKey="invoiced_amount" name="Invoiced" fill="#2563eb" />
                )}
                {!isCheckout && (
                  <Bar dataKey="collected_amount" name="Collected" fill="#16a34a" />
                )}
                {!isCheckout && (
                  <Bar dataKey="outstanding_amount" name="Outstanding" fill="#f59e0b" />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {/* By sport */}
      <section>
        <h3 className="text-lg font-medium text-gray-900 mb-3">By sport</h3>
        {(data?.by_sport?.length ?? 0) === 0 ? (
          <div className="p-4 bg-gray-50 rounded-lg text-gray-500 text-sm">
            No sport breakdown in this range
          </div>
        ) : (
          <div className="space-y-2">
            {data!.by_sport.map((row) => {
              const invoiced = row.invoiced_amount || 0;
              const collected = row.collected_amount || 0;
              const pct = isCheckout
                ? 100
                : invoiced > 0
                  ? Math.min(100, (collected / invoiced) * 100)
                  : 0;
              const active = sportCode === row.sport_code;
              return (
                <button
                  key={row.sport_code}
                  type="button"
                  onClick={() => handleSportBarClick(row.sport_code)}
                  className={`w-full text-left p-3 rounded-lg border ${
                    active
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-gray-800">
                      {conferenceSportLabel(row.sport_code)}
                    </span>
                    <span className="tabular-nums text-gray-600">
                      {isCheckout
                        ? formatInvoiceMoney(collected || invoiced, currency)
                        : `${formatInvoiceMoney(collected, currency)} / ${formatInvoiceMoney(invoiced, currency)}`}
                    </span>
                  </div>
                  <div className={`h-2 rounded-full overflow-hidden ${isCheckout ? 'bg-green-100' : 'bg-amber-100'}`}>
                    <div
                      className="h-full rounded-full bg-green-600"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Aging + chase — Overall / Invoice only */}
      {showInvoiceCollections && (
      <section ref={chaseRef}>
        <h3 className="text-lg font-medium text-gray-900 mb-3">Unpaid invoices</h3>

        <div className="flex flex-wrap gap-2 mb-3">
          {(data?.aging ?? []).map((a) => {
            const key = a.bucket as InvoiceAgingBucket;
            const active = agingFilter === key;
            return (
              <button
                key={a.bucket}
                type="button"
                onClick={() =>
                  setAgingFilter((prev) => (prev === key ? null : key))
                }
                className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                  active
                    ? 'bg-blue-600 text-white border-blue-600'
                    : a.bucket === 'overdue'
                      ? 'bg-red-50 text-red-800 border-red-200 hover:bg-red-100'
                      : a.bucket === 'due_today' || a.bucket === 'due_soon'
                        ? 'bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {AGING_LABELS[a.bucket] ?? a.bucket}: {a.count} ·{' '}
                {formatInvoiceMoney(a.amount, currency)}
              </button>
            );
          })}
        </div>

        {filteredChase.length === 0 ? (
          <div className="p-6 bg-gray-50 rounded-lg text-center text-gray-500">
            No unpaid invoices in this range
          </div>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Team
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    User
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Sports
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Amount
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Generated
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Due
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Days
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Grace
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredChase.map((row) => {
                  const overdue = (row.days_overdue ?? 0) > 0;
                  const highlight =
                    focusUnpaid && overdue
                      ? 'bg-red-50'
                      : '';
                  const search = row.username?.trim() || row.email?.trim() || '';
                  return (
                    <tr
                      key={`${row.billing_user_id}-${row.payment_id}`}
                      className={`cursor-pointer hover:bg-gray-50 ${highlight}`}
                      onClick={() => {
                        if (search) onFocusInvoiceUser(search);
                      }}
                    >
                      <td className="px-3 py-2 text-gray-800">{row.team_name || '—'}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{row.username || '—'}</div>
                        <div className="text-xs text-gray-500">{row.email}</div>
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {(row.sports ?? []).map(conferenceSportLabel).join(', ') || '—'}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-gray-900">
                        {formatInvoiceMoney(row.amount, currency)}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {formatSubscriptionDate(row.generated_at)}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {formatSubscriptionDate(row.due_date)}
                      </td>
                      <td className="px-3 py-2">
                        {overdue ? (
                          <span className="text-red-700 font-medium">
                            {row.days_overdue}d overdue
                          </span>
                        ) : row.days_until_due != null ? (
                          <span className="text-gray-700">{row.days_until_due}d left</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {row.is_grace ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900">
                            Grace
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}
        </>
      )}
    </div>
  );
};

export default AdminInvoiceAnalyticsTab;
